<template>
    <fieldset
        :class="[
            'cz2__panel',
            'cz2__menu',
            {
                'cz2__menu--with-active': selectedStep != null,
                'cz2__menu--order-type-prompt-active': showMobileOrderTypePrompt,
            }
        ]"
        :disabled="selectedStep !== null"
    >
        <div class="cz2__menu__header">
            <details-header />

            <!-- Experience Type Selector -->
            <div class="cz2__menu__experience-menu">

                <!-- Mobile Selection Prompt -->
                <div
                    v-if="showMobileOrderTypePrompt"
                    class="cz2__menu__experience-selector-wrapper cz2__type__normal"
                >
                    <div class="cz2__menu__experience-mobile-title">
                        <div>Are you customizing for an individual or a group?</div>
                    </div>

                    <!-- Individual Selector -->
                    <button
                        :class="[
                            'cz2__menu__experience-selector',
                            'cz2__type__normal',
                            {
                                'cz2__menu__experience-selector--active': !selectedGroupOrder
                            },
                        ]"
                        :disabled="!selectedGroupOrder"
                        @click="setIsGroupOrder(false)"
                    >
                        <div class="cz2__menu__experience-selector__indicator"></div>
                        {{$t('GROUP_INDIVIDUAL')}}
                    </button>

                    <!-- Group Selector -->
                    <button
                        :class="[
                            'cz2__menu__experience-selector',
                            'cz2__type__normal',
                            {
                                'cz2__menu__experience-selector--active': selectedGroupOrder
                            },
                        ]"
                        :disabled="selectedGroupOrder"
                        @click="setIsGroupOrder(true)"
                    >
                        <div class="cz2__menu__experience-selector__indicator"></div>
                        {{$t('GROUP_TITLE')}}
                    </button>

                    <!-- Mobile Description -->
                    <div class="cz2__type__tiny cz2__menu__experience-mobile-desc">
                        With Group Personalization, you can customize the names and/or numbers on individual bottles with the same design.
                    </div>
                </div>

                <!-- Individual CTA -->
                <button
                    :class="[
                        'cz2__menu__experience-button',
                        'cz2__type__small',
                        'cz2__menu__experience-button--first',
                        'cz2__menu__experience-button--single',
                        {
                            'cz2__menu__experience-button--active': !isGroupOrder
                        }
                    ]"
                    :disabled="!isGroupOrder"
                    @click="showIndividualConfirmation"
                >
                    <div class="cz2__menu__experience-icon"></div>
                    {{$t('GROUP_INDIVIDUAL')}}
                </button>

                <!-- Group CTA -->
                <button
                    :class="[
                        'cz2__menu__experience-button',
                        'cz2__type__small',
                        'cz2__menu__experience-button--group',
                        {
                            'cz2__menu__experience-button--active': isGroupOrder
                        }
                    ]"
                    :disabled="isGroupOrder"
                    @click="showGroupConfirmation"
                >
                    <div class="cz2__menu__experience-icon"></div>
                    {{$t('GROUP_TITLE')}}
                </button>
            </div>
        </div>

        <div
            :class="[
                'cz2__panel__body',
                'cz2__menu__body',
                {
                    'cz2__panel__body--hack': panelBodyHack
                }
            ]"
        >
            <!-- Single Design Menu Items -->
            <div
                class="cz2__menu__items"
                v-if="!isGroupOrder"
            >
                <!-- Product Info -->
                <menu-item
                    :title="$t('MENU_PRODUCT_INFO')"
                    type="product-info"
                    @selected="selectStep('info')"
                ></menu-item>

                <!-- Quantity Select -->
                <div v-if="!isGroupOrder" class="cz2__menu-quantity hide--below-sm">
                    <number-selector
                        :prefix="$t('QUANTITY_SELECTOR')"
                        :value="quantity"
                        :min="1"
                        :max="maxQuantity"
                        @select="setQuantity($event)"
                        @decrease="setQuantity(quantity - 1)"
                        @increase="setQuantity(quantity + 1)"
                    />
                </div>

                <p class="cz2__design-supported hide--below-sm cz2__type__small cz2__muted cz2__mb-25">
                    {{$t('DESIGN_SUPPORTED_SIDE')}}
                </p>

                <!-- Color / Design Style -->
                <menu-item
                    :title="colorStepTitle"
                    type="color"
                    :description="selectedColor"
                    :color="selectedColorHex"
                    @selected="selectStep('colors')"
                ></menu-item>

                <!-- Images -->
                <menu-item
                    :title="$t('MENU_IMAGES')"
                    type="images"
                    @selected="selectStep('images')"
                ></menu-item>

                <!-- Text -->
                <menu-item
                    :title="$t('MENU_TEXT')"
                    type="text"
                    @selected="selectStep('text')"
                ></menu-item>

                <!-- Monogram -->
                <menu-item
                    :title="$t('MENU_MONOGRAM')"
                    type="monogram"
                    @selected="selectStep('monogram')"
                ></menu-item>

                <!-- Help -->
                <menu-item
                    :title="$t('MENU_HELP')"
                    type="help"
                    @selected="selectStep('help')"
                ></menu-item>

                <!-- Save for later -->
            </div>

            <!-- Group Personalization Menu Items -->
            <div
                class="cz2__menu__items"
                v-if="isGroupOrder"
            >
                <!-- Product Info -->
                <menu-item
                    :title="$t('MENU_PRODUCT_INFO')"
                    type="product-info"
                    @selected="selectStep('info')"
                ></menu-item>

                <!-- Color / Design Style -->
                <menu-item
                    :title="colorStepTitle"
                    type="color"
                    :description="selectedColor"
                    :color="selectedColorHex"
                    @selected="selectStep('colors')"
                ></menu-item>

                <!-- Images -->
                <menu-item
                    :title="$t('MENU_IMAGES')"
                    type="images"
                    @selected="selectStep('images')"
                ></menu-item>

                <!-- Group Names / Bottle Count -->
                <menu-item
                    :title="$t('GROUP_INFORMATION')"
                    type="group"
                    @selected="selectStep('group')"
                ></menu-item>

                <!-- Text Styles -->
                <menu-item
                    :title="$t('EDIT_TEXT_STYLES')"
                    type="text"
                    @selected="selectStep('text')"
                ></menu-item>

                <!-- Help -->
                <menu-item
                    :title="$t('MENU_HELP')"
                    type="help"
                    @selected="selectStep('help')"
                ></menu-item>

                <!-- Save -->
            </div>
        </div>

        <div class="cz2__panel__actions">
            <div v-if="!isGroupOrder" class="cz2__menu-quantity-small hide--above-sm">
                <number-selector
                    :searchable="false"
                    :prefix="$t('QUANTITY_SELECTOR_SHORT')"
                    :value="quantity"
                    :min="1"
                    :max="maxQuantity"
                    @select="setQuantity($event)"
                    @decrease="setQuantity(quantity - 1)"
                    @increase="setQuantity(quantity + 1)"
                />
            </div>

            <button class="cz2__panel-action cz2__panel-action--primary"
                @click.prevent="accept"><span>{{actionCaption}}</span></button>
        </div>

        <!-- Show CTA for mobile order type selection -->
        <div class="cz2__panel__actions cz2__panel__actions--order-type">
            <button class="cz2__panel-action cz2__panel-action--primary"
                @click.prevent="closeMobileOrderTypePrompt"><span>{{$t('GET_STARTED')}}</span></button>
        </div>
    </fieldset>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__panel.cz2__menu {
            left: 0;
            bottom: 0;

            width: 100%;
            height: $sidebar-height-small;

            overflow: visible;

            background: $background-color;

            display: flex;
            flex-direction: column;

            @include respond-above(sm) {
                // display: block;
                position: absolute;

                left: auto;
                top: 0;
                right: 0;

                width: $sidebar-width-large;
                height: 100%;

                z-index: 10;

                border-left: 1px solid $border-color;
            }

            .cz2__panel__actions--order-type {
                display: none;
            }

            &.cz2__menu--order-type-prompt-active {
                @include respond-below(sm) {
                    height: auto;
                    display: block;
                }

                .cz2__menu__body,
                .cz2__panel__actions {
                    display: none;
                }

                .cz2__panel__actions--order-type {
                    display: block;
                }

                @include respond-above(sm) {
                    .cz2__menu__body,
                    .cz2__panel__actions {
                        display: flex;
                    }

                    .cz2__panel__actions--order-type {
                        display: none;
                    }
                }
            }
        }

        .cz2__design-supported {
            @include respond-above(sm) {
                margin-top: 5px;

                font-size: 18px;
                line-height: 22px;
            }
        }

        .cz2__menu__body {
            position: relative;

            flex: 1 1 0;

            min-height: $sidebar-height-small - $menu-header-height-small - $panel-actions-height-small;
            max-width: 100vw;

            overflow: auto;
            -webkit-overflow-scrolling: touch;

            @include respond-above(sm) {
                min-height: 0;

                padding: 20px 20px;
            }
        }

        .cz2__menu__header {
            width: 100%;
            // min-height: $menu-header-height-small;
            min-height: auto;

            padding: 16px 12px;

            // .cz2__details-header {
            //     display: flex;
            //     flex-direction: row;
            //     align-items: center;
            // }

            .cz2__menu__experience-mobile-title {
                margin-top: 10px;
            }

            .cz2__menu__experience-selector {
                display: flex;
                align-items: center;
                color: #000;
                margin-top: 15px;
            }

            .cz2__menu__experience-selector__indicator {
                height: 16px;
                width: 16px;
                border: 1px solid #979797;
                margin-right: 5px;
                border-radius: 50%;
                position: relative;
            }

            .cz2__menu__experience-selector--active {
                .cz2__menu__experience-selector__indicator {
                    &:after {
                        content: '';
                        width: 9px;
                        height: 9px;
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%,-50%);
                        background: #000;
                        border-radius: 50%;
                    }
                }

            }

            .cz2__menu__experience-mobile-desc {
                margin-top: 10px;
            }

            .cz2__menu__experience-button {
                display: none;
            }

            @include respond-above(sm) {
                // min-height: $menu-header-height-large;
                min-height: auto;
                padding: 30px 20px 0;

                border-bottom: 1px solid $border-color;

                // .cz2__details-header {
                //     align-items: flex-start;
                //     flex-direction: column;
                // }

                .cz2__menu__experience-selector-wrapper {
                    display: none;
                }

                .cz2__menu__experience-menu {
                    margin-top: 20px;
                }

                .cz2__menu__experience-button {
                    display: inline-flex;
                    align-items: center;
                    padding: 3px 0 7px;
                    border-bottom: 4px solid transparent;

                    &.cz2__menu__experience-button--first {
                        margin-right: 50px;
                    }

                    &:first-child {
                        margin-right: 50px;
                    }
                }

                .cz2__menu__experience-button--active {
                    border-bottom-color: #000;
                }

                .cz2__menu__experience-icon {
                    width: 26px;
                    height: 18px;
                    margin-right: 5px;
                }

                .cz2__menu__experience-button--single {
                    .cz2__menu__experience-icon {
                        width: 12px;
                        background: transparent url(../assets/order-single@2x.png) no-repeat center/contain;
                    }
                }

                .cz2__menu__experience-button--group {
                    .cz2__menu__experience-icon {
                        background: transparent url(../assets/order-group@2x.png) no-repeat center/contain;
                    }
                }
            }
        }

        .cz2__menu__items {
            display: flex;
            flex-direction: row;
            align-items: flex-start;

            height: 100%;
            min-height: 85px;
            width: 100%;

            overflow: auto;
            -webkit-overflow-scrolling: touch;

            .cz2__menu__item {
                flex: 0 0 auto;
            }

            @include respond-above(sm) {
                height: auto;
                min-height: 0;

                flex-direction: column;
            }
        }

        .cz2__menu-quantity {
            margin-bottom: 20px;

            .cz2__number-selector__select {
                width: 250px;
            }
        }

        .cz2__menu-quantity-small {
            margin-right: 15px;

            .cz2__number-selector__select {
                width: 120px;
            }
        }
    }
</style>

<script>
    import MenuItem from './MenuItem.vue';
    import DetailsHeader from './DetailsHeader.vue';
    import NumberSelector from './NumberSelector.vue';
    import PanelBodyHack from './PanelBodyHack';

    import * as constants from '../constants';

    export default {
        mixins: [PanelBodyHack],
        components: {
            MenuItem,
            DetailsHeader,
            NumberSelector,
        },
        data() {
            return {
                showErrors: false,
                selectedGroupOrder: false,
            };
        },
        computed: {
            /**
             * Current controller.
             */
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Can we build this configuration?
             */
            canBuild() {
                return this.$store.getters.canBuild;
            },

            /**
             * Can we build this configuration?
             */
            canBuildReason() {
                return this.$store.getters.canBuildReason;
            },

            /**
             * Current variants list.
             */
            variants() {
                return this.$store.state.customizer.variants;
            },

            /**
             * Current selected step.
             */
            selectedStep() {
                return this.$store.state.selectedStep;
            },

            /**
             * Color step caption.
             */
            colorStepTitle() {
                return this.$store.getters.colorStepTitle;
            },

            /**
             * Currently selected color name.
             */
            selectedColor() {
                const color = this.$store.getters.selectedColor;

                const method = this.$store.state.customizer.personalizationMethod;

                let methodName = '';

                if (method === constants.METHOD_PRINTING) {
                    methodName = this.$t('PRINTING_PRINTING');
                }

                if (method === constants.METHOD_ETCHING) {
                    methodName = this.$t('PRINTING_ETCHING');
                }

                return `${color} / ${methodName}`;
            },

            /**
             * Currently selected color hex color.
             */
            selectedColorHex() {
                return this.$store.getters.selectedColorHex;
            },

            /**
             * Product title.
             */
            productTitle() {
                if (this.viewController && this.viewController.blueprint) {
                    return this.viewController.blueprint.name;
                }

                return '';
            },

            /**
             * Subtotal unit price.
             */
            subtotalUnitPrice() {
                return this.$store.state.customizer.subtotalUnitPrice;
            },

            /**
             * Selected design side.
             */
            selectedDesignSide() {
                return this.$store.state.customizer.designSide;
            },
            /**
             * Selected design side description.
             */
            selectedDesignSideDescription() {
                const side = (this.selectedDesignSide || '').toUpperCase().replace('-', '');

                return this.$t(`DESIGN_SIDE_${side}_DESCRIPTION`);
            },

            /**
             * Calculates action caption depending on whether we are editing a recipe or a fresh new build.
             */
            actionCaption() {
                if (this.$store.state.customizer.options.recipe && this.$store.state.customizer.options.recipe !== 'null') {
                    return this.$t('ACTION_UPDATE');
                }

                return this.$t('ACTION_DONE');
            },

            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },

            showMobileOrderTypePrompt() {
                return this.$store.state.showMobileOrderTypePrompt;
            },

            quantity() {
                return this.$store.state.customizer.quantity;
            },

            maxQuantity() {
                // return Math.min(150, this.$store.getters.availableQuantity);
                return this.$store.getters.availableQuantity;
            },

            /**
             * Currently selected bottle.
             */
            selectedBottle() {
                return this.$store.state.customizer.selectedBottle;
            },

            /*
             * All bottles list.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles || [];
            },
        },
        methods: {
            /**
             * Saves the design.
             */
            save() {
            },

            /**
             * Prepares the design for adding to cart.
             */
            accept() {
                this.$store.dispatch('selectStep', 'review');
            },

            /**
             * Selects a step.
             */
            selectStep(step) {
                if (this.isGroupOrder) {
                    // Switch to the primary bottle first.
                    if (this.secondaryBottles.indexOf(this.selectedBottle) !== 0) {
                        this.$store.dispatch('setSelectedBottle', this.secondaryBottles[0]).then(() => {
                            this.$store.dispatch('selectStep', step);
                        });
                    } else {
                        this.$store.dispatch('selectStep', step);
                    }
                } else {
                    this.$store.dispatch('selectStep', step);
                }
            },

            /**
             * Set group order type
             */
            setIsGroupOrder(isGroupOrder) {
                this.selectedGroupOrder = isGroupOrder;
            },

            /**
             * Show the group confirmation modal
             */
            showGroupConfirmation() {
                this.$store.dispatch('showGroupConfirmation', true);
            },

            /**
             * Show the group confirmation modal
             */
            showIndividualConfirmation() {
                this.$store.dispatch('showIndividualConfirmation', true);
            },

            /**
             * Close order type prompt
             */
            closeMobileOrderTypePrompt() {
                if (this.isGroupOrder !== this.selectedGroupOrder) {
                    this.$store.dispatch('setIsGroupOrder', this.selectedGroupOrder);
                }

                this.$store.dispatch('closeMobileOrderTypePrompt');

                this.$nextTick(() => {
                    // Reset scroll position.
                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                });
            },

            setQuantity(v) {
                this.$store.dispatch('setQuantity', v);
            },
        },
    };
</script>
